import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AccountLayout from '../components/account/AccountLayout';
import AccountNav from '../components/account/AccountNav';
import { useAuth } from '../context/AuthContext';
import { useAccount } from '../hooks/useAccount';
import { saveKey } from '../services/keys';
import { useQueryClient } from '@tanstack/react-query';

const AccountIntegrationsPage = () => {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const { data: account, isLoading: accountLoading, error: accountError } = useAccount(accessToken);

  const [editing, setEditing] = useState({ openai: false, anthropic: false });
  const [values, setValues] = useState({ openai: '', anthropic: '' });
  const [savingProvider, setSavingProvider] = useState(null);
  const [keyMessage, setKeyMessage] = useState('');
  const [keyError, setKeyError] = useState('');
  const rowRefs = useRef({ openai: null, anthropic: null });
  const [localKeys, setLocalKeys] = useState({ openai: null, anthropic: null });

  const keyData = useMemo(() => {
    const base = {};
    (account?.keys || []).forEach((k) => {
      if (k?.provider) base[k.provider] = k;
    });
    return {
      openai: localKeys.openai || base.openai || {},
      anthropic: localKeys.anthropic || base.anthropic || {},
    };
  }, [account, localKeys]);

  // click-away to cancel editing
  useEffect(() => {
    const handleClick = (e) => {
      const nextEditing = { ...editing };
      let changed = false;
      Object.entries(rowRefs.current).forEach(([prov, ref]) => {
        if (editing[prov] && ref && !ref.contains(e.target)) {
          nextEditing[prov] = false;
          setValues((prev) => ({ ...prev, [prov]: '' }));
          changed = true;
        }
      });
      if (changed) setEditing(nextEditing);
    };
    const anyEditing = Object.values(editing).some(Boolean);
    if (anyEditing) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [editing]);

  const handleSave = async (event, provider) => {
    event.preventDefault();
    if (!accessToken) {
      setKeyError('Missing access token');
      return;
    }
    if (!values[provider]) {
      setKeyError('Please paste your key first');
      return;
    }
    setSavingProvider(provider);
    setKeyError('');
    setKeyMessage('');
    try {
      await saveKey(accessToken, { provider, key: values[provider] });
      setKeyMessage('Key saved');
      setValues((prev) => ({ ...prev, [provider]: '' }));
      setLocalKeys((prev) => ({
        ...prev,
        [provider]: { present: true, last4: values[provider].slice(-4) },
      }));
      queryClient.invalidateQueries({ queryKey: ['account'] });
      setEditing((prev) => ({ ...prev, [provider]: false }));
    } catch (err) {
      setKeyError(err.message || 'Unable to save key');
    } finally {
      setSavingProvider(null);
    }
  };

  const handleNav = (key) => {
    if (key === 'account') navigate('/account');
    else if (key === 'integrations') navigate('/account/integrations');
  };

  return (
    <AccountLayout
      nav={
        <AccountNav
          name={`${account?.profile?.first_name || account?.first_name || ''} ${
            account?.profile?.last_name || account?.last_name || ''
          }`}
          activeKey="integrations"
          onSelect={handleNav}
          onBack={() => navigate('/')}
        />
      }
    >
      <header>
        <h1 className="text-2xl font-semibold text-slate-50">Integrations</h1>
      </header>
      <div className="space-y-4">
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-50">API Keys</h2>
          </div>
          {accountLoading && (
            <p className="text-sm text-slate-300 text-center w-full">Loading account…</p>
          )}
          {accountError && (
            <p className="text-sm text-red-300 text-center w-full">
              {accountError.message || 'Unable to load account'}
            </p>
          )}
          {!accountLoading && !accountError && (
            <div className="space-y-3">
              {['openai', 'anthropic'].map((prov) => {
                const present = !!keyData[prov]?.present;
                const isEditing = editing[prov] || !present;
                const disabled = !isEditing || savingProvider === prov;
                const displayValue = isEditing
                  ? values[prov]
                  : present
                    ? '***************' + (keyData[prov]?.last4 ? ` ${keyData[prov].last4}` : '')
                    : '';
                const displayType = isEditing ? 'password' : 'text';
                return (
                  <form
                    key={prov}
                    className="flex items-center gap-2"
                    ref={(el) => {
                      rowRefs.current[prov] = el;
                    }}
                    onSubmit={(e) => handleSave(e, prov)}
                  >
                    <label className="w-28 text-sm font-semibold capitalize text-slate-200">{prov}</label>
                    <input
                      type={displayType}
                      readOnly={!isEditing}
                      value={displayValue}
                      disabled={!isEditing}
                      onChange={(e) => setValues((prev) => ({ ...prev, [prov]: e.target.value }))}
                      className={`flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-slate-100 outline-none ${
                        isEditing
                          ? 'focus:border-sky-400/60 focus:ring-2 focus:ring-sky-400/30'
                          : 'opacity-70 w-1/2 min-w-[160px]'
                      }`}
                      placeholder="Paste your key"
                      required
                    />
                    <button
                      type="submit"
                      disabled={disabled}
                      className="rounded-lg bg-gradient-to-r from-amber-500 to-sky-500 px-4 py-2 text-sm font-semibold text-[#050815] shadow-lg shadow-sky-500/30 disabled:opacity-40"
                    >
                      {savingProvider === prov ? 'Saving…' : 'Save'}
                    </button>
                    {present && !isEditing && (
                      <button
                        type="button"
                        onClick={() => setEditing((prev) => ({ ...prev, [prov]: true }))}
                        className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-white/10"
                      >
                        Edit
                      </button>
                    )}
                  </form>
                );
              })}
            </div>
          )}
          {keyMessage && <p className="mt-2 text-sm text-emerald-300">{keyMessage}</p>}
          {keyError && <p className="mt-2 text-sm text-red-300">{keyError}</p>}
        </div>
      </div>
    </AccountLayout>
  );
};

export default AccountIntegrationsPage;
